import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, FileText, Clock, CheckCircle } from "lucide-react";
import Button from "../../common/Button";
import Card from "../../common/Card";
import FormField from "../../common/FormField";
import { useMemberApi } from "../../../hooks/useMemberApi";
import { getUserName } from "../../../utils/tokenUtils";
import handleChange from "../../../utils/formUtils/handleChange";
import { MemberDTO } from "../../../services/apiClient";

interface FormData {
    idNumber: string;
    terms: boolean;
}

interface Props {
    currentStep?: number;
    member: MemberDTO | undefined;
    onNext: () => void;
    onPrevious: () => void;
}

const EvaluationInstructions: React.FC<Props> = ({ currentStep, member, onNext, onPrevious }) => {

    const userName = getUserName() || '';

    const [formData, setFormData] = useState<FormData>({
        idNumber: '',
        terms: false
    });

    const [idDoesNotExistError, setIdDoesNotExistError] = useState<string>('');
    const [isIdValid, setIsIdValid] = useState<boolean>(false);

    const canProceed = formData.idNumber && formData.terms && !idDoesNotExistError;

    const handleNext = async () => {
        if (!formData.idNumber || !formData.terms) {
            return;
        }

        // Validate ID when button is clicked
        const isValid = await handleIdExists(formData.idNumber);

        if (isValid) {
            window.scrollTo({ top: 0 });
            onNext();
        }
    };

    const handlePrevious = () => {
        window.scrollTo({ top: 0 });
        onPrevious();
    };

    //CALL USE API HOOK => HERE CHECK IF EXISTS
    const { checkIdExists, isLoading } = useMemberApi();
    const handleIdExists = useCallback(async (id: string): Promise<boolean> => {
        try {
            const idExistsResponse = await checkIdExists(id);
            if (idExistsResponse?.data === false) {
                setIdDoesNotExistError('ID Number does not exist in the system');
                setIsIdValid(false);
                return false;
            } else {
                const accountIdPassport = (member?.idPassportNumber || '').trim();
                const enteredId = id.trim();

                if (!accountIdPassport) {
                    setIdDoesNotExistError('Unable to verify your account ID number. Please contact support.');
                    setIsIdValid(false);
                    return false;
                }

                if (accountIdPassport !== enteredId) {
                    setIdDoesNotExistError('ID Number does not match your account details');
                    setIsIdValid(false);
                    return false;
                }

                setIdDoesNotExistError('');
                setIsIdValid(true);
                return true;
            }
        } catch (error) {
            console.error('Error checking ID existence:', error);
            setIdDoesNotExistError('Unable to validate ID number. Please try again.');
            setIsIdValid(false);
            return false;
        }
    }, [checkIdExists, member]);

    const handleIdNumberChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const value = event.target.value;

        // Update form data first
        setFormData({ ...formData, idNumber: value });

        // Clear any previous errors when user starts typing
        if (idDoesNotExistError) {
            setIdDoesNotExistError('');
        }
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setFormData({ ...formData, terms: checked });
    };

    return (
        <>
            <Card title="Evaluation Instructions" subtitle="Please read carefully before starting the evaluation">

                <div className="mb-6">
                    <div className="flex items-center mb-3">
                        <Clock className="h-5 w-5 text-blue-600 mr-2" />
                        <h3 className="font-semibold text-lg">Time Limit & Format</h3>
                    </div>
                    <p className="mb-4">
                        You have <strong>60 minutes</strong> to complete this questionnaire, starting when you click "I Agree and Start".<br />
                        Read each question carefully and select the correct answer: <strong>(a), (b), (c)</strong>, etc., or mark <strong>TRUE/FALSE</strong> as required.
                    </p>
                </div>

                <div className="mb-6">
                    <div className="flex items-center mb-3">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <h3 className="font-semibold text-lg">Assessment Details</h3>
                    </div>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>Pass mark: <strong>70%</strong></li>
                        <li>No "trick" questions - read carefully and answer honestly</li>
                        <li>All content is covered in the Dedicated Award Scheme Material</li>
                        <li>Open book assessment - you may refer to your <strong>printed manual</strong> or <strong>downloadable material</strong></li>
                    </ul>
                </div>

                <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-6">
                    <div className="flex items-center">
                        <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                        <p className="text-red-700 font-semibold">
                            <strong>IMPORTANT:</strong> Do NOT refresh the page or press F5 during the evaluation - this will cancel your assessment immediately!
                        </p>
                    </div>
                </div>

            </Card>

            <Card title="Personal statement and agreement" subtitle={`Complete Below`}>

                <FormField
                    id="idNumber"
                    label={`Identity Confirmation - ${userName}`}
                    type="text"
                    value={formData.idNumber}
                    onChange={handleIdNumberChange}
                    placeholder={isLoading ? "Validating..." : "Enter your 13-digit SA ID Number"}
                    required={false}
                    error={idDoesNotExistError}
                    disabled={isLoading}
                />

                <p className="mt-2 text-sm text-gray-600">
                    I, <strong>{userName}</strong>, confirm that I am the person writing this evaluation to attain dedicated status with Natshoot (NHSA).
                </p>

                <div className="mt-6">
                    <div className="flex items-center mb-3">
                        <FileText className="h-5 w-5 text-gray-600 mr-2" />
                        <h3 className="font-semibold text-lg">Terms and Conditions</h3>
                    </div>
                    <p className="mb-3">I also confirm the following:</p>
                    <ul className="list-disc list-inside ml-4 mt-2 space-y-2">
                        <li>I am completing this evaluation personally - no one else is writing it on my behalf</li>
                        <li>I am not receiving assistance from another person (unless special arrangements were made with Natshoot Exco)</li>
                        <li>I do not have any answer sheets or memorandums with me during this evaluation</li>
                        <li>I understand that legal action may be taken if I violate any of the above terms</li>
                        <li>This agreement is effective from the moment I begin the evaluation</li>
                        <li>I consent to providing electronic data for identity verification (used only to prevent fraud)</li>
                        <li>I understand that <strong>fraud charges may apply</strong> if someone else completes this evaluation in my name</li>
                        <li>I may lose my membership and accreditation with Natshoot (NHSA) if I violate any of the above terms</li>
                    </ul>
                </div>

                <br /><br />

                <FormField
                    id="terms"
                    label="I have read and understand all terms and conditions above, and I agree to comply with them."
                    type="checkbox"
                    value={formData.terms}
                    onChange={(e) => handleChange(e, setFormData)}
                    required={false}
                />

                {/* Navigation Buttons */}
                <div className={`p-6 flex ${currentStep === 1 ? 'justify-end' : 'justify-between'}`}>

                    <Button onClick={handlePrevious} variant="secondary">
                        Back
                    </Button>

                    <Button
                        onClick={handleNext}
                        variant={canProceed && !isLoading ? "success" : "disabled"}
                        disabled={!canProceed || isLoading}
                    >
                        {isLoading ? 'Validating...' : 'I Agree and Start'}
                    </Button>
                </div>

            </Card>
        </>
    );
};
export default EvaluationInstructions;